/**
 * auth-roles.ts — pure, environment-agnostic role logic (WS-K).
 *
 * Single source of truth for:
 *   - the role HIERARCHY rank (contributor < writer < editor < admin < owner)
 *   - the STAFF allow-list (who may enter /admin)
 *   - meetsMinimumRole() used by both the client AuthContext and any server guard
 *
 * Pure functions only (no React, no firebase, no next/*) so they are trivially
 * unit-testable and safe to import on the server OR the client.
 */

import type { UserRole } from '@/types/permissions';

/**
 * Ordered rank. contributor (user-world) sits BELOW the staff ladder. Higher = more
 * privileged. Unknown/legacy roles resolve to 0 so they never satisfy a gate.
 *
 * `member` (WS-K L3) is a FRACTIONAL rank (1.5) deliberately inserted ABOVE contributor and BELOW
 * writer WITHOUT renumbering the staff ranks (2/3/4/5). This keeps every existing
 * meetsMinimumRole() comparison byte-for-byte unchanged (a member still fails any "writer+" gate
 * and still passes any "contributor-or-above" gate), while letting member out-rank contributor.
 * Member is STILL user-world only — it is deliberately EXCLUDED from STAFF_ROLES below, so
 * isStaffRole('member')===false and a member never enters /admin.
 */
export const ROLE_RANK: Record<UserRole, number> = {
  contributor: 1,
  member: 1.5,
  writer: 2,
  editor: 3,
  admin: 4,
  owner: 5,
};

/**
 * Roles permitted in the STAFF world (/admin). 'contributor' (and any future user-world
 * role such as 'member') is deliberately excluded — those live in /contribute.
 */
export const STAFF_ROLES: readonly UserRole[] = ['writer', 'editor', 'admin', 'owner'];

/** Rank for any role string, defaulting unknown/legacy values to 0 (never passes a gate). */
export function rankOf(role: string | undefined | null): number {
  if (!role) return 0;
  return ROLE_RANK[role as UserRole] ?? 0;
}

/** True iff `role` is at or above `minimumRole` in the hierarchy. */
export function meetsMinimumRole(
  role: string | undefined | null,
  minimumRole: UserRole,
): boolean {
  return rankOf(role) >= rankOf(minimumRole);
}

/** True iff `role` may access the staff world (/admin). */
export function isStaffRole(role: string | undefined | null): boolean {
  return !!role && (STAFF_ROLES as readonly string[]).includes(role);
}

// ── Capability predicates (AH-2) ────────────────────────────────────────────
// The route layer used to spell authority as inline exact-string arrays
// (`requireRole(user, ['editor','admin','owner'])`, `[ADMIN, OWNER]`). Those bake the
// role ENUM into dozens of call sites, so retiring a role means editing all of them and
// risks a self-403. These two predicates name the CAPABILITY instead; the enum lives in
// one place (ROLE_RANK) and the retire step touches only the predicate body.

/** Admin authority: admin or owner (the `[ADMIN, OWNER]` gate). */
export function isAdminRole(role: string | undefined | null): boolean {
  return meetsMinimumRole(role, 'admin');
}

/**
 * Editorial ASSERT authority — may commit a review/correction/lead verdict (content-review,
 * corrections desk, lead-flags). Today: editor-or-above = {editor, admin, owner}, byte-identical
 * to the exact-string `['editor','admin','owner']` arrays it replaces. When `editor` is retired
 * (post-migration) this COLLAPSES to `isAdminRole` — delete the `role === 'editor'` clause and no
 * route changes. `writer`/`member`/`contributor`/unknown all fail, exactly as before.
 */
export function canAssert(role: string | undefined | null): boolean {
  return isAdminRole(role) || role === 'editor';
}

// ── Assignable-role allowlist (AH-2) ─────────────────────────────────────────
// Every role a user-management write may mint. Derived from ROLE_RANK so it can NEVER drift
// from the hierarchy (retiring a role from ROLE_RANK auto-removes it here). The write seams
// (user-management-service createUser/updateUser) validate against this so a garbage or injected
// role string ('superadmin', typo, '') can never be persisted.
export const VALID_ROLES: readonly UserRole[] = Object.freeze(
  Object.keys(ROLE_RANK) as UserRole[],
);

/** True iff `role` is an assignable role (narrows to UserRole). */
export function isValidRole(role: string | undefined | null): role is UserRole {
  return !!role && (VALID_ROLES as readonly string[]).includes(role);
}
